import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Lock, ShieldCheck } from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { useCurrentReseller } from '../../api/reseller';

/** Reseller invite-link landing page — set initial password and optionally enable 2FA. */
export function ResellerSetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [enable2fa, setEnable2fa] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { data: currentReseller } = useCurrentReseller();
  if (!currentReseller) return null;

  const accent = currentReseller.primaryColor;
  const mismatch = confirm.length > 0 && confirm !== password;
  const tooShort = password.length > 0 && password.length < 8;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mismatch || tooShort || !password) return;
    setSubmitting(true);
    setTimeout(() => navigate({ to: '/reseller/onboarding' }), 800);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark relative flex items-center justify-center px-4 py-10">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        <AuthThemeToggle />
      </div>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <span
            className="w-12 h-12 rounded-xl grid place-items-center text-white font-semibold text-lg shadow-sm"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          >
            {currentReseller.displayName.charAt(0)}
          </span>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Welcome, Rakib
          </h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300 text-center max-w-sm">
            Set a password for your reseller account at{' '}
            <span className="font-medium text-zinc-800 dark:text-zinc-100">
              {currentReseller.slug}.itcitysoft.com
            </span>
            .
          </p>
        </div>

        <Card className="p-6 sm:p-7">
          <form onSubmit={onSubmit} className="space-y-5">
            <Input
              label="Password"
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint="Use at least 8 characters with letters and numbers"
              error={tooShort ? 'Must be at least 8 characters' : undefined}
            />

            <Input
              label="Confirm password"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={mismatch ? 'Passwords don’t match' : undefined}
            />

            <label className="flex items-start gap-3 p-3 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 cursor-pointer">
              <input
                type="checkbox"
                checked={enable2fa}
                onChange={(e) => setEnable2fa(e.target.checked)}
                className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600"
                style={{ accentColor: accent }}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-950 dark:text-zinc-50">
                  <ShieldCheck className="w-4 h-4" style={{ color: accent }} />
                  Enable 2FA now
                  <span className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
                    Recommended
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                  You'll scan a QR with your authenticator app on the next step.
                </p>
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting || !password || mismatch || tooShort}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md text-white font-medium px-4 py-2.5 text-sm transition disabled:opacity-50"
              style={{ backgroundColor: accent }}
            >
              {submitting ? 'Saving…' : 'Continue'}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>

            <p className="flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <Lock className="w-3 h-3" />
              Link expires 48 hours after invitation
            </p>
          </form>
        </Card>

        <div className="mt-8 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
          <span>Powered by</span>
          <span className="font-semibold text-zinc-700 dark:text-zinc-200">Possignin</span>
        </div>

        {/* Imported but only referenced here to satisfy bundler; rendered via custom button */}
        <div className="hidden">
          <Button />
        </div>
      </div>
    </div>
  );
}
